import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { LayoutGrid, LogOut } from 'lucide-react';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { cn } from '@/ui/cn';
import { useBackToClose } from '@/ui/useBackToClose';
import { groupItems } from './navGroups';

/** How far the sheet must be dragged down before letting go closes it. */
const DISMISS_PX = 90;

/**
 * The console on a phone, built like an app rather than a shrunk website:
 *
 * - a tab bar along the bottom with the first four places and "More"; the
 *   active tab gets a pill behind its icon, as native tab bars do;
 * - "More" (or the avatar in the top bar) raises a bottom sheet: who is
 *   signed in, every place as a tappable tile, and sign out. It is dismissed
 *   by dragging it down, tapping outside, Escape, or going somewhere.
 *
 * `open` is owned by DashLayout because the avatar opens the same sheet.
 */
export function DashMobileNav({ navItems, title, open, onOpen, onClose }) {
  const { pathname } = useLocation();
  // Items flagged `tab` are the role's four everyday places; without flags,
  // the first four live items stand in.
  const live = navItems.filter((item) => !item.disabled);
  const flagged = live.filter((item) => item.tab);
  const tabs = (flagged.length ? flagged : live).slice(0, 4);

  // Going anywhere closes the sheet.
  useEffect(() => {
    onClose();
  }, [pathname, onClose]);

  return (
    <>
      <nav
        aria-label="Main"
        className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-surface/95 pb-[env(safe-area-inset-bottom)] backdrop-blur-md md:hidden"
      >
        <div className="flex h-16 items-stretch">
          {tabs.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className="group flex min-w-0 flex-1 flex-col items-center justify-center gap-1"
            >
              {({ isActive }) => (
                <>
                  <span
                    className={cn(
                      'relative grid h-8 w-14 place-items-center rounded-full transition-colors duration-200',
                      isActive
                        ? 'bg-brand-50 text-brand-500'
                        : 'text-content-muted group-active:bg-surface-muted',
                    )}
                  >
                    {item.icon && <item.icon className="h-[21px] w-[21px]" aria-hidden="true" />}
                    {item.badge > 0 && (
                      <span className="absolute -right-0.5 -top-1 min-w-[18px] rounded-full bg-accent-500 px-1 text-center text-[10px] font-bold leading-[18px] text-white ring-2 ring-surface">
                        {item.badge}
                      </span>
                    )}
                  </span>
                  <span
                    className={cn(
                      'max-w-full truncate px-1 text-[11px]',
                      isActive ? 'font-bold text-brand-500' : 'font-semibold text-content-muted',
                    )}
                  >
                    {/* One word fits under an icon on the narrowest phones. */}
                    {item.short ?? item.label.split(' ')[0]}
                  </span>
                </>
              )}
            </NavLink>
          ))}
          <button
            type="button"
            onClick={onOpen}
            aria-expanded={open}
            aria-haspopup="dialog"
            className="group flex min-w-0 flex-1 flex-col items-center justify-center gap-1"
          >
            <span
              className={cn(
                'grid h-8 w-14 place-items-center rounded-full transition-colors duration-200',
                open
                  ? 'bg-brand-50 text-brand-500'
                  : 'text-content-muted group-active:bg-surface-muted',
              )}
            >
              <LayoutGrid className="h-[21px] w-[21px]" aria-hidden="true" />
            </span>
            <span
              className={cn(
                'text-[11px]',
                open ? 'font-bold text-brand-500' : 'font-semibold text-content-muted',
              )}
            >
              More
            </span>
          </button>
        </div>
      </nav>

      <MoreSheet navItems={navItems} title={title} open={open} onClose={onClose} />
    </>
  );
}

function MoreSheet({ navItems, title, open, onClose }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const sheetRef = useRef(null);
  const dragStart = useRef(null);
  const [dragY, setDragY] = useState(0);
  const replace = useBackToClose(open, onClose);

  useEffect(() => {
    if (!open) {
      setDragY(0);
      return undefined;
    }
    const onKey = (event) => event.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    sheetRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [open, onClose]);

  // Drag the handle area down to dismiss, like a native sheet.
  const onTouchStart = (event) => {
    dragStart.current = event.touches[0].clientY;
  };
  const onTouchMove = (event) => {
    if (dragStart.current == null) return;
    setDragY(Math.max(0, event.touches[0].clientY - dragStart.current));
  };
  const onTouchEnd = () => {
    if (dragY > DISMISS_PX) onClose();
    else setDragY(0);
    dragStart.current = null;
  };

  async function handleLogout() {
    await logout();
    navigate('/', { replace: true });
  }

  const initials = (user?.full_name ?? '?')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join('');
  const dragging = dragStart.current != null;

  return (
    // Hidden (not just off-screen) while closed, so its links are not reachable
    // by keyboard or screen reader; visibility flips after the slide-out.
    <div
      className={cn(
        'fixed inset-0 z-50 transition-[visibility] duration-300 md:hidden',
        !open && 'pointer-events-none invisible',
      )}
    >
      <div
        className={cn(
          'absolute inset-0 bg-ink/40 transition-opacity duration-300',
          open ? 'opacity-100' : 'opacity-0',
        )}
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-label="More"
        tabIndex={-1}
        className={cn(
          'absolute inset-x-0 bottom-0 flex max-h-[88vh] flex-col rounded-t-[28px] bg-surface pb-[env(safe-area-inset-bottom)] shadow-lift outline-none',
          !dragging && 'transition-transform duration-300 ease-[cubic-bezier(.2,.8,.2,1)]',
          open ? 'translate-y-0' : 'translate-y-full',
        )}
        style={open && dragY ? { transform: `translateY(${dragY}px)` } : undefined}
      >
        <div
          className="shrink-0 touch-none px-5 pb-3 pt-2.5"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <span
            className="mx-auto mb-4 block h-1.5 w-10 rounded-full bg-border"
            aria-hidden="true"
          />
          <div className="flex items-center gap-3 rounded-2xl bg-surface-muted p-3">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-accent-500 text-[15px] font-bold text-white">
              {initials}
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate font-semibold text-content">{user?.full_name}</p>
              <p className="truncate text-[12.5px] text-content-muted">{user?.email}</p>
              <span className="mt-1 inline-block rounded-md bg-brand-50 px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-[0.06em] text-brand-500">
                {title}
              </span>
            </div>
          </div>
        </div>

        <nav aria-label="All pages" className="no-scrollbar flex-1 overflow-y-auto px-5 pb-2">
          {groupItems(navItems).map((group, index) => (
            <section key={group.heading ?? `group-${index}`} className="mt-3">
              {group.heading && (
                <h6 className="mb-2 px-1 text-[11px] font-bold uppercase tracking-[0.1em] text-content-muted">
                  {group.heading}
                </h6>
              )}
              <div className="grid grid-cols-3 gap-2.5">
                {group.items.map((item) =>
                  item.disabled ? (
                    <span
                      key={item.label}
                      aria-disabled="true"
                      className="relative flex flex-col items-center gap-1.5 rounded-2xl border border-dashed border-border px-1 py-3 text-center text-[12px] font-semibold text-content-muted opacity-60"
                    >
                      {item.icon && <item.icon className="h-[22px] w-[22px]" aria-hidden="true" />}
                      <span className="line-clamp-2 leading-tight">{item.label}</span>
                      <span className="text-[9.5px] font-bold uppercase tracking-[0.06em]">
                        {item.disabledTag ?? 'Soon'}
                      </span>
                    </span>
                  ) : (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.end}
                      // The page takes the sheet's Back entry; and a tap on
                      // the page already on screen still closes the sheet.
                      replace={replace}
                      onClick={onClose}
                      className={({ isActive }) =>
                        cn(
                          'relative flex flex-col items-center gap-1.5 rounded-2xl border px-1 py-3 text-center text-[12px] font-semibold transition-[transform,background-color] active:scale-[0.97]',
                          isActive
                            ? 'border-brand-500 bg-brand-50 text-brand-500'
                            : 'border-border bg-surface text-content',
                        )
                      }
                    >
                      {item.icon && <item.icon className="h-[22px] w-[22px]" aria-hidden="true" />}
                      <span className="line-clamp-2 leading-tight">{item.label}</span>
                      {item.badge > 0 && (
                        <span className="absolute right-2 top-2 min-w-[18px] rounded-full bg-accent-500 px-1 text-center text-[10px] font-bold leading-[18px] text-white">
                          {item.badge}
                        </span>
                      )}
                    </NavLink>
                  ),
                )}
              </div>
            </section>
          ))}
        </nav>

        <div className="shrink-0 border-t border-border px-5 py-3">
          <button
            type="button"
            onClick={handleLogout}
            className="flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-danger/10 text-[15px] font-semibold text-danger transition-transform active:scale-[0.98]"
          >
            <LogOut className="h-[18px] w-[18px]" aria-hidden="true" />
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
